import {
  normalizeRecipientOption,
  uniqueRecipientsByKey,
} from '@/composables/people/recipientOptions';
import emailMsgsService from '@/services/emailMsgs';
import { sendEmailInvoiceContextConfig } from '@/composables/send/sendEmailInvoiceContext';
import { sendEmailPaymentReceiptContextConfig } from '@/composables/send/sendEmailPaymentReceiptContext';
import { sendEmailExpenseReceiptContextConfig } from '@/composables/send/sendEmailExpenseReceiptContext';
import { sendEmailSimpleNoteContextConfig } from '@/composables/send/sendEmailSimpleNoteContext';
import { sendEmailInvoiceSectionMethods } from '@/composables/send/sendEmailInvoiceSectionMethods';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { findOptionByRawValue } from '@/utils/optionValues';
import { resolveApiPayload } from '@/utils/dataNormalization';
import { normalizeEmail, isValidEmail } from '@/utils/emailValue';
import { resolveText } from '@/utils/textValue';

const extractMessageText = (value = '') => {
  return String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const escapeRegExp = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeTemplateSpacing = (value = '') => {
  return String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const resolveTemplateMessageValue = (payload = {}) => {
  const root = payload && typeof payload === 'object' ? payload : {};
  const hasEnvelopeData = Object.prototype.hasOwnProperty.call(root, 'data');
  const rawData = root?.data;
  const row = Array.isArray(rawData)
    ? (rawData[0] && typeof rawData[0] === 'object' ? rawData[0] : null)
    : (rawData && typeof rawData === 'object' ? rawData : null);

  const nestedData = row?.data;
  const nestedRow = Array.isArray(nestedData)
    ? (nestedData[0] && typeof nestedData[0] === 'object' ? nestedData[0] : null)
    : (nestedData && typeof nestedData === 'object' ? nestedData : null);

  const source = nestedRow && typeof nestedRow === 'object'
    ? nestedRow
    : (row && typeof row === 'object' ? row : null);

  const directRootSource = (!source && !hasEnvelopeData && root && typeof root === 'object')
    ? root
    : null;

  const resolvedSource = source || directRootSource;

  if (!resolvedSource) {
    return '';
  }

  const candidates = [
    resolvedSource?.message_html,
    resolvedSource?.message,
    resolvedSource?.msg,
    resolvedSource?.template,
    resolvedSource?.body,
  ];

  const found = candidates.find((candidate) => {
    return typeof candidate === 'string' && candidate.trim() !== '';
  });

  return String(found || '');
};

const SEND_EMAIL_CONTEXT_CONFIGS = Object.freeze({
  invoice: sendEmailInvoiceContextConfig,
  payment_receipt: sendEmailPaymentReceiptContextConfig,
  expense_receipt: sendEmailExpenseReceiptContextConfig,
  simple_note: sendEmailSimpleNoteContextConfig,
});

export const createSendEmailModalState = () => ({
  text: {},
  summaryMeta: {},
  primaryRecipient: {},
  recipientOptions: [],
  initialToRecipientIds: [],
  initialCcRecipientIds: [],
  recipientsLoading: false,
  copyOptions: [],
  documentAttachmentOptions: [],
  invoiceAttachmentOptions: [],
  messageType: '',
  messageTypeCandidates: [],
  messageVariables: {},
  messageLoadingText: '',
  initialMessage: '',
  messageHtml: '',
  messageText: '',
  toRecipients: [],
  ccRecipients: [],
  toQuery: '',
  ccQuery: '',
  activeRecipientField: '',
  closeRecipientMenuTimer: null,
  selectedDocumentAttachments: [],
  selectedInvoiceAttachments: [],
  newDocumentUploads: [],
  documentAttachmentMode: 'existing',
  newDocumentUploadCounter: 0,
  showDocumentAttachments: false,
  showInvoiceAttachments: false,
});

export const sendEmailModalComputed = {
  sendEmailContextType() {
    return this.contextType;
  },
  supportsInvoiceAttachments() {
    const contextConfig = SEND_EMAIL_CONTEXT_CONFIGS[this.sendEmailContextType];
    return Boolean(contextConfig?.supportsInvoiceAttachments);
  },
  canSelectInvoiceAttachments() {
    const contextConfig = SEND_EMAIL_CONTEXT_CONFIGS[this.sendEmailContextType];
    if (!contextConfig || typeof contextConfig !== 'object') {
      return this.supportsInvoiceAttachments;
    }

    if (Object.prototype.hasOwnProperty.call(contextConfig, 'allowInvoiceAttachmentSelection')) {
      return Boolean(contextConfig.allowInvoiceAttachmentSelection);
    }

    return this.supportsInvoiceAttachments;
  },
  resolvedText() {
    return this.text && typeof this.text === 'object' ? this.text : {};
  },
  summaryData() {
    const summary = this.summaryMeta && typeof this.summaryMeta === 'object'
      ? this.summaryMeta
      : {};

    const summaryText = this.resolvedText?.summary || {};

    return {
      sendTypeLabel: resolveText(summary.sendTypeLabel, resolveText(summaryText.sendTypeLabel, '')),
      sendTypeValue: resolveText(summary.sendTypeValue, resolveText(summaryText.sendTypeValue, '')),
      counterpartLabel: resolveText(
        summary.counterpartLabel,
        resolveText(summaryText.counterpartLabel || summaryText.customerLabel || summaryText.supplierLabel, '')
      ),
      counterpartValue: resolveText(summary.counterpartValue, ''),
      totalLabel: resolveText(summary.totalLabel, resolveText(summaryText.totalLabel, '')),
      totalValue: resolveText(summary.totalValue, ''),
    };
  },
  normalizedPrimaryRecipient() {
    const name = resolveText(this.primaryRecipient?.name || this.primaryRecipient?.label || '', '');
    const email = resolveText(this.primaryRecipient?.email || '', '');

    return {
      id: resolveText(this.primaryRecipient?.id || '', ''),
      name,
      email,
    };
  },
  normalizedToRecipientOptions() {
    const fromProps = Array.isArray(this.recipientOptions) ? this.recipientOptions : [];
    const candidates = [
      ...fromProps,
      this.primaryRecipient,
    ];

    const normalized = candidates
      .map((item, index) => normalizeRecipientOption(item, index, 'recipient-option'))
      .filter((item) => item && item.email !== '');

    return uniqueRecipientsByKey(normalized);
  },
  normalizedCcRecipientOptions() {
    const copyOptions = Array.isArray(this.copyOptions) ? this.copyOptions : [];
    const normalized = copyOptions
      .map((item, index) => normalizeRecipientOption(item, index, 'cc-option'))
      .filter((item) => item && item.email !== '');

    return uniqueRecipientsByKey(normalized);
  },
  normalizedDocumentAttachmentOptions() {
    return (Array.isArray(this.documentAttachmentOptions) ? this.documentAttachmentOptions : [])
      .map((item, index) => {
        if (!item || typeof item !== 'object') {
          const [normalizedOption] = normalizeCatalogOptions([item], { useCodeInLabel: false });
          if (!normalizedOption) {
            return null;
          }

          return {
            id: String(normalizedOption?.value || `doc-${index}`).trim() || `doc-${index}`,
            label: String(normalizedOption?.label || '').trim(),
            document_id: 0,
            source_origin: '',
            is_file: 0,
          };
        }

        const id = String(item?.id ?? item?.document_id ?? item?.value ?? `doc-${index}`).trim();
        const label = String(item?.label ?? item?.name ?? item?.title ?? '').trim();
        if (label === '') {
          return null;
        }

        return {
          id: id !== '' ? id : `doc-${index}`,
          label,
          document_id: Number(item?.document_id || item?.id || 0) || 0,
          source_origin: String(item?.source_origin || '').trim(),
          is_file: Number(item?.is_file || 0) === 1 ? 1 : 0,
        };
      })
      .filter(Boolean);
  },
  normalizedInvoiceAttachmentOptions() {
    return (Array.isArray(this.invoiceAttachmentOptions) ? this.invoiceAttachmentOptions : []).filter(Boolean);
  },
  selectedDocumentAttachmentItems() {
    return (Array.isArray(this.selectedDocumentAttachments) ? this.selectedDocumentAttachments : [])
      .map((option) => ({ ...option, group: 'document-existing' }));
  },
  selectedNewDocumentAttachmentItems() {
    return (Array.isArray(this.newDocumentUploads) ? this.newDocumentUploads : [])
      .filter((upload) => upload && upload.file)
      .map((upload) => ({
        id: String(upload.id),
        label: String(upload.fileName || upload.file?.name || '').trim(),
        group: 'document-new',
        file: upload.file,
      }));
  },
  selectedInvoiceAttachmentItems() {
    if (!this.supportsInvoiceAttachments) {
      return [];
    }

    const contextConfig = this.resolveContextConfig();
    if (contextConfig && typeof contextConfig.resolveSelectedInvoiceAttachmentItems === 'function') {
      return contextConfig.resolveSelectedInvoiceAttachmentItems(this);
    }

    return [];
  },
  defaultInvoiceAttachmentItem() {
    if (!this.supportsInvoiceAttachments) {
      return null;
    }

    const contextConfig = this.resolveContextConfig();
    if (contextConfig && typeof contextConfig.resolveDefaultInvoiceAttachmentItem === 'function') {
      return contextConfig.resolveDefaultInvoiceAttachmentItem(this);
    }

    return null;
  },
  selectedAttachmentItems() {
    return [
      ...this.selectedDocumentAttachmentItems,
      ...this.selectedNewDocumentAttachmentItems,
      ...this.selectedInvoiceAttachmentItems,
    ];
  },
  summaryAttachmentItems() {
    const defaultItem = this.defaultInvoiceAttachmentItem;

    if (!defaultItem || typeof defaultItem !== 'object') {
      return this.selectedAttachmentItems;
    }

    return [defaultItem, ...this.selectedAttachmentItems];
  },
  isExistingDocumentMode() {
    return this.documentAttachmentMode === 'existing';
  },
  summaryToValue() {
    if (this.toRecipients.length === 0) {
      return '';
    }

    return this.toRecipients.map((item) => item.label).join(', ');
  },
  summaryCopyValue() {
    if (this.ccRecipients.length === 0) {
      return this.resolvedText.noCopiesSelected;
    }

    return this.ccRecipients.map((item) => item.label).join(', ');
  },
  normalizedToQueryEmail() {
    return normalizeEmail(this.toQuery);
  },
  normalizedCcQueryEmail() {
    return normalizeEmail(this.ccQuery);
  },
  filteredToOptions() {
    return this.filterRecipientOptionsByQuery(this.toQuery, this.normalizedToRecipientOptions);
  },
  filteredCcOptions() {
    return this.filterRecipientOptionsByQuery(this.ccQuery, this.normalizedCcRecipientOptions);
  },
  canCreateExternalTo() {
    if (!this.allowExternalRecipients || !isValidEmail(this.normalizedToQueryEmail)) {
      return false;
    }

    return !this.hasRecipientByEmail('to', this.normalizedToQueryEmail);
  },
  canCreateExternalCc() {
    if (!this.allowExternalRecipients || !isValidEmail(this.normalizedCcQueryEmail)) {
      return false;
    }

    return !this.hasRecipientByEmail('cc', this.normalizedCcQueryEmail);
  },
  canSubmit() {
    const hasRecipient = this.toRecipients.length > 0;
    const hasMessage = String(this.messageText || extractMessageText(this.messageHtml)).trim() !== '';
    return hasRecipient && hasMessage;
  },
};

export const sendEmailModalMethods = {
  resolveContextConfig() {
    return SEND_EMAIL_CONTEXT_CONFIGS[this.sendEmailContextType] || null;
  },
  resolveSendEmailTextByContext() {
    const contextConfig = this.resolveContextConfig();
    if (!contextConfig || typeof contextConfig.resolveText !== 'function') {
      return {};
    }

    return contextConfig.resolveText(this);
  },
  async initializeContextState() {
    this.text = this.resolveSendEmailTextByContext();
    this.summaryMeta = {};
    this.primaryRecipient = {};
    this.recipientOptions = [];
    this.initialToRecipientIds = [];
    this.initialCcRecipientIds = [];
    this.recipientsLoading = false;
    this.copyOptions = [];
    this.documentAttachmentOptions = [];
    this.invoiceAttachmentOptions = [];
    this.messageType = '';
    this.messageTypeCandidates = [];
    this.messageVariables = {};
    this.messageLoadingText = '';
    this.initialMessage = '';

    const contextConfig = this.resolveContextConfig();
    if (contextConfig && typeof contextConfig.hydrateContextData === 'function') {
      await contextConfig.hydrateContextData(this);
    }

    this.resetForm();
    this.loadMessageTemplate();
  },
  buildNewDocumentUploadRow() {
    this.newDocumentUploadCounter += 1;

    return {
      id: this.newDocumentUploadCounter,
      file: null,
      fileName: '',
    };
  },
  handleMessageTextUpdate(value) {
    const normalizedValue = String(value || '').trim();
    this.messageText = normalizedValue;
  },
  applyInitialMessage(nextMessage = '') {
    const normalizedMessage = String(nextMessage || '');
    this.messageHtml = normalizedMessage;
    this.messageText = extractMessageText(normalizedMessage);
  },
  applyTemplateVariables(template = '') {
    let resolvedTemplate = String(template || '');
    const variables = this.messageVariables && typeof this.messageVariables === 'object'
      ? this.messageVariables
      : {};

    Object.entries(variables).forEach(([rawKey, rawValue]) => {
      const key = String(rawKey || '').trim();
      const value = resolveText(rawValue, '');

      if (key === '' || value === '') {
        return;
      }

      const escapedKey = escapeRegExp(key);
      const moustachePattern = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'gi');
      const bracketPattern = new RegExp(`\\[\\s*${escapedKey}\\s*\\]`, 'gi');

      resolvedTemplate = resolvedTemplate
        .replace(moustachePattern, value)
        .replace(bracketPattern, value);
    });

    return normalizeTemplateSpacing(resolvedTemplate);
  },
  async loadMessageTemplate() {
    const configuredCandidates = Array.isArray(this.messageTypeCandidates)
      ? this.messageTypeCandidates
      : [];
    const fallbackType = String(this.messageType || '').trim();
    const candidateTypes = Array.from(new Set([
      ...configuredCandidates.map((item) => String(item || '').trim()),
      fallbackType,
    ].filter((item) => item !== '')));

    if (candidateTypes.length <= 0) {
      this.applyInitialMessage(this.initialMessage);
      return;
    }

    try {
      let serviceMessage = '';

      for (const candidateType of candidateTypes) {
        const response = await emailMsgsService.byType(
          { type: candidateType },
          { loadingText: this.messageLoadingText || 'Cargando...' }
        );
        const payload = resolveApiPayload(response);
        serviceMessage = resolveTemplateMessageValue(payload).trim();

        if (serviceMessage !== '') {
          break;
        }
      }

      if (serviceMessage === '') {
        this.applyInitialMessage(this.applyTemplateVariables(this.initialMessage));
        return;
      }

      const resolvedMessage = this.applyTemplateVariables(serviceMessage);
      this.applyInitialMessage(resolvedMessage);

      this.$nextTick(() => {
        this.applyInitialMessage(resolvedMessage);
      });
    } catch (error) {
      this.applyInitialMessage(this.applyTemplateVariables(this.initialMessage));
    }
  },
  resetForm() {
    this.applyInitialMessage(this.initialMessage);
    this.initializeRecipients();
    this.selectedDocumentAttachments = [];
    this.selectedInvoiceAttachments = [];
    this.documentAttachmentMode = 'existing';
    this.newDocumentUploadCounter = 0;
    this.newDocumentUploads = [this.buildNewDocumentUploadRow()];
    this.showDocumentAttachments = false;
    this.showInvoiceAttachments = false;
    this.activeRecipientField = '';
    this.toQuery = '';
    this.ccQuery = '';
  },
  initializeRecipients() {
    const toInitial = this.resolveInitialRecipientsByIds(this.initialToRecipientIds, this.normalizedToRecipientOptions);
    const ccInitial = this.resolveInitialRecipientsByIds(this.initialCcRecipientIds, this.normalizedCcRecipientOptions);

    this.toRecipients = uniqueRecipientsByKey(toInitial);
    this.ccRecipients = uniqueRecipientsByKey(ccInitial).filter((item) => {
      return !this.toRecipients.some((toItem) => {
        const sameId = String(toItem?.id || '') === String(item?.id || '');
        const sameEmail = normalizeEmail(toItem?.email || '') !== ''
          && normalizeEmail(toItem?.email || '') === normalizeEmail(item?.email || '');

        return sameId || sameEmail;
      });
    });

    if (this.toRecipients.length === 0 && this.normalizedPrimaryRecipient.email !== '') {
      const fallbackRecipient = normalizeRecipientOption(this.normalizedPrimaryRecipient, 0, 'primary-fallback');
      if (fallbackRecipient) {
        this.toRecipients = [fallbackRecipient];
      }
    }
  },
  resolveInitialRecipientsByIds(ids = [], options = []) {
    const normalizedIds = (Array.isArray(ids) ? ids : []).map((item) => String(item || '').trim()).filter((item) => item !== '');
    const sourceOptions = Array.isArray(options) ? options : [];

    if (normalizedIds.length === 0 || sourceOptions.length === 0) {
      return [];
    }

    return normalizedIds
      .map((id) => findOptionByRawValue(sourceOptions, id, { optionKeys: ['email'] }))
      .filter(Boolean);
  },
  clearRecipientMenuTimer() {
    if (this.closeRecipientMenuTimer) {
      clearTimeout(this.closeRecipientMenuTimer);
      this.closeRecipientMenuTimer = null;
    }
  },
  scheduleRecipientMenuClose() {
    this.clearRecipientMenuTimer();
    this.closeRecipientMenuTimer = setTimeout(() => {
      this.activeRecipientField = '';
    }, 120);
  },
  showRecipientMenu(field) {
    return this.activeRecipientField === field;
  },
  openRecipientMenu(field) {
    this.clearRecipientMenuTimer();
    this.activeRecipientField = field;
  },
  focusRecipientField(field) {
    this.openRecipientMenu(field);

    this.$nextTick(() => {
      const refName = field === 'cc' ? 'ccInput' : 'toInput';
      const inputRef = this.$refs[refName];
      if (inputRef && typeof inputRef.focus === 'function') {
        inputRef.focus();
      }
    });
  },
  selectedRecipientsByField(field) {
    return field === 'cc' ? this.ccRecipients : this.toRecipients;
  },
  setRecipientsByField(field, recipients) {
    if (field === 'cc') {
      this.ccRecipients = uniqueRecipientsByKey(recipients);
      return;
    }

    this.toRecipients = uniqueRecipientsByKey(recipients);
  },
  filterRecipientOptionsByQuery(rawQuery = '', sourceOptions = []) {
    const query = String(rawQuery || '').trim().toLowerCase();
    const options = Array.isArray(sourceOptions) ? sourceOptions : [];

    if (query === '') {
      return options;
    }

    return options.filter((option) => {
      return String(option.name || '').toLowerCase().includes(query)
        || String(option.email || '').toLowerCase().includes(query);
    });
  },
  hasRecipientByEmail(field, email) {
    const normalizedEmail = normalizeEmail(email);
    if (normalizedEmail === '') {
      return false;
    }

    return this.selectedRecipientsByField(field).some((item) => normalizeEmail(item.email) === normalizedEmail);
  },
  isRecipientSelected(field, recipientId) {
    return this.selectedRecipientsByField(field).some((item) => String(item.id) === String(recipientId));
  },
  addRecipient(field, recipient) {
    const normalizedRecipient = normalizeRecipientOption(recipient, 0, `${field}-selected`);
    if (!normalizedRecipient || normalizedRecipient.email === '') {
      return;
    }

    if (this.isRecipientSelected(field, normalizedRecipient.id) || this.hasRecipientByEmail(field, normalizedRecipient.email)) {
      return;
    }

    const nextRecipients = [...this.selectedRecipientsByField(field), normalizedRecipient];
    this.setRecipientsByField(field, nextRecipients);
  },
  removeRecipient(field, recipientId) {
    const filtered = this.selectedRecipientsByField(field).filter((item) => String(item.id) !== String(recipientId));
    this.setRecipientsByField(field, filtered);
  },
  toggleRecipientFromMenu(field, recipient) {
    const isSelected = this.isRecipientSelected(field, recipient.id);
    if (isSelected) {
      this.removeRecipient(field, recipient.id);
    } else {
      this.addRecipient(field, recipient);
    }

    this.focusRecipientField(field);
  },
  handleRecipientBackspace(field, event) {
    const query = field === 'cc' ? this.ccQuery : this.toQuery;
    if (String(query || '').trim() !== '') {
      return;
    }

    const recipients = this.selectedRecipientsByField(field);
    if (recipients.length === 0) {
      return;
    }

    const lastRecipient = recipients[recipients.length - 1];
    this.removeRecipient(field, lastRecipient.id);
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
  },
  addExternalRecipient(field) {
    const email = field === 'cc' ? this.normalizedCcQueryEmail : this.normalizedToQueryEmail;
    if (!isValidEmail(email)) {
      return;
    }

    this.addRecipient(field, {
      id: `external-${field}-${email}`,
      name: email,
      email,
      isExternal: true,
    });

    if (field === 'cc') {
      this.ccQuery = '';
    } else {
      this.toQuery = '';
    }
  },
  confirmRecipientInput(field) {
    if (field === 'cc') {
      if (this.canCreateExternalCc) {
        this.addExternalRecipient('cc');
      }

      return;
    }

    if (this.canCreateExternalTo) {
      this.addExternalRecipient('to');
    }
  },
  removeAttachment(attachment) {
    if (!attachment || typeof attachment !== 'object') {
      return;
    }

    if (attachment.group === 'invoice') {
      this.selectedInvoiceAttachments = this.selectedInvoiceAttachments.filter((item) => String(item?.id) !== String(attachment.id));
      return;
    }

    if (attachment.group === 'document-new') {
      this.removeNewDocumentUploadRow(attachment.id);
      return;
    }

    this.selectedDocumentAttachments = this.selectedDocumentAttachments.filter((item) => {
      return String(item?.id || '') !== String(attachment.id || '');
    });
  },
  setDocumentAttachmentMode(mode) {
    if (mode !== 'existing' && mode !== 'new') {
      return;
    }

    this.documentAttachmentMode = mode;
  },
  openNewDocumentPicker(uploadId) {
    const inputRef = this.$refs[`newDocInput-${uploadId}`];
    const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

    if (input && typeof input.click === 'function') {
      input.click();
    }
  },
  handleNewDocumentChange(uploadId, event) {
    const [file] = event?.target?.files || [];
    const targetId = String(uploadId);

    this.newDocumentUploads = this.newDocumentUploads.map((upload) => {
      if (String(upload.id) !== targetId) {
        return upload;
      }

      return {
        ...upload,
        file: file || null,
        fileName: file?.name || '',
      };
    });
  },
  addNewDocumentUploadRow() {
    this.newDocumentUploads = [...this.newDocumentUploads, this.buildNewDocumentUploadRow()];
  },
  removeNewDocumentUploadRow(uploadId) {
    const targetId = String(uploadId);
    const filtered = this.newDocumentUploads.filter((upload) => String(upload.id) !== targetId);

    if (filtered.length > 0) {
      this.newDocumentUploads = filtered;
      return;
    }

    this.newDocumentUploadCounter = 0;
    this.newDocumentUploads = [this.buildNewDocumentUploadRow()];
  },
  ...sendEmailInvoiceSectionMethods,
  emitClose() {
    if (this.submitting) {
      return;
    }

    this.$emit('close');
  },
  emitSubmit() {
    if (this.submitting || !this.canSubmit) {
      return;
    }

    this.$emit('submit', {
      type: this.sendEmailContextType,
      context: this.contextData,
      to: this.toRecipients,
      cc: this.ccRecipients,
      attachments: {
        documents: {
          existing: this.selectedDocumentAttachmentItems,
          new: this.selectedNewDocumentAttachmentItems,
          all: [
            ...this.selectedDocumentAttachmentItems,
            ...this.selectedNewDocumentAttachmentItems,
          ],
        },
        invoices: this.selectedInvoiceAttachmentItems,
        all: this.selectedAttachmentItems,
      },
      messageHtml: this.messageHtml,
      messageText: this.messageText,
    });
  },
};
